/**
 * Receipt Generator — creates a clean branded receipt as a downloadable PNG.
 * Zero dependencies: uses HTML Canvas API.
 */

const PRIMARY = '#063B33';
const ACCENT = '#F5C232';
const TEXT_MAIN = '#1A1A1A';
const TEXT_SEC = '#7D8C88';
const BG = '#F7F9F8';
const WHITE = '#FFFFFF';

const STATUS_COLORS = {
  AWAITING_FUNDS: '#B58500',
  FUNDS_RECEIVED: '#0066CC',
  PROCESSING: '#6B21A8',
  SENT: '#0066CC',
  COMPLETED: '#078830',
  FAILED: '#e72e08',
};

const STATUS_LABELS = {
  AWAITING_FUNDS: 'Awaiting Funds',
  FUNDS_RECEIVED: 'Funds Received',
  PROCESSING: 'Processing',
  SENT: 'Sent',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
};

const PAYOUT_LABELS = {
  BANK: 'Bank Transfer',
  MOBILE_MONEY: 'Mobile Money',
  CASH_PICKUP: 'Cash Pickup',
  bank_transfer: 'Bank Transfer',
  mobile_money: 'Mobile Money',
  cash_pickup: 'Cash Pickup',
};

const formatCurrency = (amount, currency) => {
  if (amount == null) return '—';
  const n = typeof amount === 'number' ? amount : parseFloat(amount);
  if (isNaN(n)) return '—';
  const fmt = n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  if (currency === 'NGN') return '\u20A6' + fmt;
  if (currency === 'CFA' || currency === 'XOF') return 'CFA ' + fmt;
  return fmt;
};

const formatDate = (d) => {
  if (!d) return new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/**
 * Generate and download a receipt PNG for a transaction.
 * @param {Object} tx - Transaction data (from Firestore or success object)
 *   Required: reference, amount, inputCurrency, outputAmount, outputCurrency, rate, status
 *   Optional: recipientName, recipientBank, recipientAccount, recipientPhone, payoutMethod, createdAt
 */
export function generateReceipt(tx) {
  const dpr = Math.min(window.devicePixelRatio || 1, 3);
  const W = 420;

  // Collect rows
  const rows = [];
  rows.push({ label: 'Reference', value: tx.reference || '—' });
  rows.push({ label: 'Date', value: formatDate(tx.createdAt) });
  rows.push({ label: 'You Send', value: formatCurrency(tx.amount, tx.inputCurrency) });
  rows.push({ label: 'You Receive', value: formatCurrency(tx.outputAmount, tx.outputCurrency) });
  rows.push({ label: 'Exchange Rate', value: tx.rate ? `1 ${tx.inputCurrency} = ${tx.rate} ${tx.outputCurrency}` : '—' });

  const payoutMethod = PAYOUT_LABELS[tx.payoutMethod] || 'Bank Transfer';
  rows.push({ label: 'Payout Method', value: payoutMethod });

  if (tx.recipientName) rows.push({ label: 'Recipient', value: tx.recipientName });
  if (tx.recipientBank) rows.push({ label: tx.payoutMethod === 'mobile_money' || tx.payoutMethod === 'MOBILE_MONEY' ? 'Network' : 'Bank', value: tx.recipientBank });
  if (tx.recipientAccount) rows.push({ label: 'Account', value: tx.recipientAccount });
  if (tx.recipientPhone) rows.push({ label: 'Phone', value: tx.recipientPhone });
  if (tx.pickupCode) rows.push({ label: 'Pickup Code', value: tx.pickupCode });

  // Calculate height
  const headerH = 90;
  const statusH = 50;
  const rowH = 36;
  const padding = 28;
  const footerH = 60;
  const H = headerH + statusH + (rows.length * rowH) + padding * 2 + footerH + 20;

  const canvas = document.createElement('canvas');
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);

  // Background
  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, W, H);

  // Header bar
  ctx.fillStyle = PRIMARY;
  roundRect(ctx, 0, 0, W, headerH, 0);
  ctx.fill();

  // Logo text
  ctx.fillStyle = WHITE;
  ctx.font = 'bold 22px Inter, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('KwikX', W / 2, 38);
  ctx.font = '12px Inter, system-ui, sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  ctx.fillText('Transaction Receipt', W / 2, 58);

  // Accent line
  ctx.fillStyle = ACCENT;
  ctx.fillRect(W / 2 - 30, 70, 60, 3);

  // Card background
  const cardY = headerH + 10;
  const cardH = H - headerH - 20;
  ctx.fillStyle = WHITE;
  roundRect(ctx, 16, cardY, W - 32, cardH, 12);
  ctx.fill();
  ctx.strokeStyle = '#E5E7EB';
  ctx.lineWidth = 1;
  roundRect(ctx, 16, cardY, W - 32, cardH, 12);
  ctx.stroke();

  // Status badge
  const statusColor = STATUS_COLORS[tx.status] || TEXT_SEC;
  const statusLabel = STATUS_LABELS[tx.status] || tx.status || 'Pending';
  let y = cardY + padding;
  ctx.textAlign = 'center';
  ctx.font = 'bold 13px Inter, system-ui, sans-serif';
  ctx.fillStyle = statusColor;
  const badgeW = ctx.measureText(statusLabel).width + 24;
  const badgeX = W / 2 - badgeW / 2;
  ctx.globalAlpha = 0.12;
  ctx.fillStyle = statusColor;
  roundRect(ctx, badgeX, y, badgeW, 28, 14);
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.fillStyle = statusColor;
  ctx.fillText(statusLabel, W / 2, y + 19);
  y += 44;

  // Rows
  ctx.textAlign = 'left';
  const leftX = 36;
  const rightX = W - 36;

  rows.forEach((row, i) => {
    // Zebra bg
    if (i % 2 === 0) {
      ctx.fillStyle = BG;
      ctx.fillRect(24, y - 4, W - 48, rowH);
    }

    ctx.fillStyle = TEXT_SEC;
    ctx.font = '12px Inter, system-ui, sans-serif';
    ctx.fillText(row.label, leftX, y + 14);

    ctx.fillStyle = TEXT_MAIN;
    ctx.font = '600 13px Inter, system-ui, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(row.value, rightX, y + 14);
    ctx.textAlign = 'left';

    y += rowH;
  });

  // Divider
  y += 8;
  ctx.strokeStyle = '#E5E7EB';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(36, y);
  ctx.lineTo(W - 36, y);
  ctx.stroke();
  y += 16;

  // Footer
  ctx.textAlign = 'center';
  ctx.fillStyle = TEXT_SEC;
  ctx.font = '10px Inter, system-ui, sans-serif';
  ctx.fillText('Generated on ' + new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }), W / 2, y + 6);
  ctx.fillText('kwikx.com • Powered by KwikX', W / 2, y + 22);

  // Download
  canvas.toBlob((blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `KwikX-Receipt-${tx.reference || 'transaction'}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 'image/png');
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
